import { useQuery } from "@apollo/client";
import { Edit } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Typography } from "@mui/material";
import { FormikValues } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  BulkInviteResults,
  CreateSubaccountUserInviteMutation,
  CreateSubaccountUserInviteMutationVariables,
  CreateSubaccountUserInvitesMutation,
  CreateSubaccountUserInvitesMutationVariables,
  ListSubaccountUserInvitesQuery,
  ListSubaccountUserInvitesQueryVariables,
  ListSubaccountUsersQuery,
  ListSubaccountUsersQueryVariables,
  RemoveUserFromSubaccountMutation,
  RemoveUserFromSubaccountMutationVariables,
  RevokeSubaccountUserInviteMutation,
  RevokeSubaccountUserInviteMutationVariables,
  UpdateUserSubaccountRoleMutation,
  UpdateUserSubaccountRoleMutationVariables,
  UserSortBy,
  UserSubaccountRole,
  UserTenantRole,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { ExternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox } from "~frontend/components/Table";
import InviteDialog from "~frontend/components/users/InviteDialog";
import UserInviteTable from "~frontend/components/users/UserInviteTable";
import UserListControls from "~frontend/components/users/UserListControls";
import UserTable from "~frontend/components/users/UserTable";
import {
  SUBACCOUNT_ROLE_DISPLAY_NAMES,
  TENANT_ROLE_DISPLAY_NAMES,
  USER_INVITE_SORTS,
  USER_SORTS,
} from "~frontend/components/users/constants";
import {
  SubaccountUserInvite,
  SubaccountUser,
  UserStatus,
} from "~frontend/components/users/types";
import {
  CREATE_SUBACCOUNT_USER_INVITE,
  CREATE_SUBACCOUNT_USER_INVITES,
  LIST_SUBACCOUNT_USERS,
  LIST_SUBACCOUNT_USER_INVITES,
  REMOVE_USER_FROM_SUBACCOUNT,
  REVOKE_SUBACCOUNT_USER_INVITE,
  UPDATE_USER_SUBACCOUNT_ROLE,
} from "~frontend/graphql/users";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUser } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

import { EditUserDialog } from "./EditUserDialog";
import { InviteLinkDialog } from "./InviteLinkDialog";

const ADD_NEW_USERS_SUPPORT_LINK =
  "https://support.scaletowin.com/adding-new-users-user-level-definitions-in-scale-to-win-text";

export default function SubaccountUsers(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;

  useBreadcrumbs([{ label: "People", path: subaccountLink("/users") }]);
  const snackbar = useSnackbar();

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "pageSize",
    withDefault(NumberParam, 10)
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    "status",
    withDefault(StringParam, UserStatus.ACTIVE)
  );
  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(
      StringParam,
      statusFilter === UserStatus.INVITED
        ? USER_INVITE_SORTS[0].value
        : USER_SORTS[0].value
    )
  );

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLinkDialogOpen, setInviteLinkDialogOpen] = useState(false);
  const [bulkInviteResults, setBulkInviteResults] =
    useState<BulkInviteResults | null>(null);

  const [userToEdit, setUserToEdit] = useState<SubaccountUser | null>(null);

  const currentUser = useCurrentUser()!;

  const listUserVariables = {
    input: {
      subaccountId: subaccount.id,
      sort: sort as UserSortBy,
      filter: { nameOrEmailContains: search },
      limit: pageSize,
      offset: page * pageSize,
    },
    subaccountId: subaccount.id,
  };

  const listUsersResult = useQuery<
    ListSubaccountUsersQuery,
    ListSubaccountUsersQueryVariables
  >(LIST_SUBACCOUNT_USERS, {
    variables: listUserVariables,
    pollInterval: 60 * 1000,
  });

  const listUserInvitesResult = useQuery<
    ListSubaccountUserInvitesQuery,
    ListSubaccountUserInvitesQueryVariables
  >(LIST_SUBACCOUNT_USER_INVITES, {
    variables: listUserVariables,
    pollInterval: 60 * 1000,
  });

  const [createSubaccountUserInvite] = useMutation<
    CreateSubaccountUserInviteMutation,
    CreateSubaccountUserInviteMutationVariables
  >(CREATE_SUBACCOUNT_USER_INVITE, {
    refetchQueries: [
      { query: LIST_SUBACCOUNT_USERS, variables: listUserVariables },
      { query: LIST_SUBACCOUNT_USER_INVITES, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const [createSubaccountUserInvites] = useMutation<
    CreateSubaccountUserInvitesMutation,
    CreateSubaccountUserInvitesMutationVariables
  >(CREATE_SUBACCOUNT_USER_INVITES, {
    refetchQueries: [
      { query: LIST_SUBACCOUNT_USERS, variables: listUserVariables },
      { query: LIST_SUBACCOUNT_USER_INVITES, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const [revokeSubaccountUserInvite] = useMutation<
    RevokeSubaccountUserInviteMutation,
    RevokeSubaccountUserInviteMutationVariables
  >(REVOKE_SUBACCOUNT_USER_INVITE, {
    refetchQueries: [
      { query: LIST_SUBACCOUNT_USER_INVITES, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const [removeUserFromSubaccount] = useMutation<
    RemoveUserFromSubaccountMutation,
    RemoveUserFromSubaccountMutationVariables
  >(REMOVE_USER_FROM_SUBACCOUNT, {
    refetchQueries: [
      { query: LIST_SUBACCOUNT_USERS, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const [updateUserSubaccountRole] = useMutation<
    UpdateUserSubaccountRoleMutation,
    UpdateUserSubaccountRoleMutationVariables
  >(UPDATE_USER_SUBACCOUNT_ROLE, {
    refetchQueries: [
      { query: LIST_SUBACCOUNT_USERS, variables: listUserVariables },
      { query: LIST_SUBACCOUNT_USER_INVITES, variables: listUserVariables },
    ],
    awaitRefetchQueries: true,
  });

  const handleInviteUser = useCallback(
    async ({
      email,
      fullName,
      role,
      emails,
      multipleInvitesEnabled,
    }: {
      email?: string;
      fullName?: string;
      role: UserSubaccountRole;
      emails?: string[];
      multipleInvitesEnabled?: boolean;
    }) => {
      if (multipleInvitesEnabled && emails) {
        const { data, errors } = await createSubaccountUserInvites({
          variables: {
            input: {
              subaccountId: subaccount.id,
              emails,
              subaccountRole: role,
            },
          },
        });

        if (errors) {
          snackbar.enqueueSnackbar(
            `Error inviting users: ${errors[0].message}`,
            { variant: "error" }
          );
          throw errors;
        }
        // switch dialog to "results" panel
        setBulkInviteResults(data!.createSubaccountUserInvites);
      } else {
        const { data, errors } = await createSubaccountUserInvite({
          variables: {
            input: {
              subaccountId: subaccount.id,
              email: email as string,
              fullName: fullName as string,
              subaccountRole: role,
            },
          },
        });

        if (errors) {
          snackbar.enqueueSnackbar(
            `Error inviting user: ${errors[0].message}`,
            { variant: "error" }
          );
          throw errors;
        } else if (data?.createSubaccountUserInvite.user) {
          snackbar.enqueueSnackbar(
            `Role added to user ${data.createSubaccountUserInvite.user.fullName}`
          );
        } else {
          snackbar.enqueueSnackbar(`User ${fullName} invited`);
        }
      }
    },
    [
      createSubaccountUserInvite,
      createSubaccountUserInvites,
      snackbar,
      subaccount,
    ]
  );

  const handleRevokeInvite = useCallback(
    async (userInvite: SubaccountUserInvite) => {
      const { errors } = await revokeSubaccountUserInvite({
        variables: {
          input: { userInviteId: userInvite.id, subaccountId: subaccount.id },
        },
      });

      if (errors) {
        snackbar.enqueueSnackbar(
          `Error revoking invite: ${errors[0].message}`,
          { variant: "error" }
        );
      } else {
        snackbar.enqueueSnackbar(`Invite revoked for ${userInvite.email}`);
      }
    },
    [revokeSubaccountUserInvite, subaccount, snackbar]
  );

  const handleRemoveUser = useCallback(
    async (user: SubaccountUser) => {
      const { errors } = await removeUserFromSubaccount({
        variables: { input: { userId: user.id, subaccountId: subaccount.id } },
      });

      if (errors) {
        snackbar.enqueueSnackbar(
          `Error deactivating user: ${errors[0].message}`,
          { variant: "error" }
        );
      } else {
        snackbar.enqueueSnackbar(`User ${user.email} deactivated`);
      }
    },
    [removeUserFromSubaccount, subaccount, snackbar]
  );

  const handleUpdateUserRole = async (value: FormikValues) => {
    if (!userToEdit) {
      throw new Error("No user selected");
    }

    const { errors } = await updateUserSubaccountRole({
      variables: {
        input: {
          userId: userToEdit.id,
          role: value.role,
          subaccountId: subaccount.id,
        },
      },
    });

    if (errors) {
      snackbar.enqueueSnackbar(`Error updating user: ${errors[0].message}`, {
        variant: "error",
      });
    } else {
      snackbar.enqueueSnackbar(`User updated`);
    }
    setUserToEdit(null);
  };

  const renderTable = () => {
    switch (statusFilter) {
      case UserStatus.INVITED:
        if (listUserInvitesResult.loading) {
          return <Loading />;
        }
        if (listUserInvitesResult.error) {
          return <ErrorCard errors={[listUserInvitesResult.error]} />;
        }
        return (
          <UserInviteTable
            userInvites={
              listUserInvitesResult.data!.listSubaccountUserInvites.userInvites
            }
            totalCount={
              listUserInvitesResult.data!.listSubaccountUserInvites.totalCount
            }
            page={page}
            onChangePage={setPage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={newPageSize => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            renderRole={userInvite => {
              if (
                userInvite.tenantRole === UserTenantRole.None &&
                userInvite.subaccountMemberships.length > 0
              ) {
                return SUBACCOUNT_ROLE_DISPLAY_NAMES[
                  userInvite.subaccountMemberships[0].role
                ];
              } else {
                return TENANT_ROLE_DISPLAY_NAMES[userInvite.tenantRole];
              }
            }}
            renderActions={userInvite => [
              {
                id: "revoke",
                icon: <DeleteIcon />,
                tooltipLabel: "Revoke invite",
                onClick: () => handleRevokeInvite(userInvite),
                confirm: {
                  title: "Revoke invite",
                  description: `Revoke invite to ${userInvite.email}?`,
                },
                disabled: userInvite.tenantRole !== UserTenantRole.None,
              },
            ]}
          />
        );
      case UserStatus.ACTIVE:
        if (listUsersResult.loading) {
          return <Loading />;
        }
        if (listUsersResult.error) {
          return <ErrorCard errors={[listUsersResult.error]} />;
        }
        return (
          <UserTable
            users={listUsersResult.data!.listSubaccountUsers.users}
            totalCount={listUsersResult.data!.listSubaccountUsers.totalCount}
            page={page}
            onChangePage={setPage}
            rowsPerPage={pageSize}
            onChangeRowsPerPage={newPageSize => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            renderRole={user => {
              if (user.tenantRole === UserTenantRole.None) {
                return SUBACCOUNT_ROLE_DISPLAY_NAMES[
                  user.subaccountMemberships[0].role
                ];
              } else {
                return TENANT_ROLE_DISPLAY_NAMES[user.tenantRole];
              }
            }}
            renderActions={user => {
              const deactivateDisabled =
                user.tenantRole === UserTenantRole.Staff ||
                user.tenantRole === UserTenantRole.Owner ||
                user.id === currentUser.id;

              const editAction =
                user.tenantRole === UserTenantRole.None
                  ? [
                      {
                        id: "edit",
                        icon: <Edit />,
                        onClick: () => setUserToEdit(user),
                      },
                    ]
                  : [];

              return [
                ...editAction,
                {
                  id: "deactivate",
                  icon: <DeleteIcon />,
                  tooltipLabel: deactivateDisabled ? undefined : "Deactivate",
                  onClick: () => handleRemoveUser(user),
                  confirm: {
                    title: "Deactivate user",
                    description: `Deactivate ${user.email}?`,
                  },
                  disabled: deactivateDisabled,
                },
              ];
            }}
          />
        );
    }
  };

  const editRoleOptions = [];
  for (const [role, label] of Object.entries(SUBACCOUNT_ROLE_DISPLAY_NAMES)) {
    editRoleOptions.push({ label, value: role as UserSubaccountRole });
  }

  return (
    <PageContainer>
      <PageHeader isSlim>People</PageHeader>
      <Typography variant="h2" sx={{ mb: 2 }}>
        You can invite new users to your subaccount and change the role of
        existing users. For more information about what permissions each user
        role has, see{" "}
        <ExternalLink href={ADD_NEW_USERS_SUPPORT_LINK}>
          our help article here.
        </ExternalLink>
      </Typography>
      <UserListControls
        statusFilter={statusFilter as UserStatus}
        setStatusFilter={newFilter => {
          setStatusFilter(newFilter);
          setPage(0);
        }}
        search={search}
        setSearch={newSearch => {
          setSearch(newSearch);
          setPage(0);
        }}
        sort={sort}
        setSort={setSort}
        onClickInvite={() => setInviteDialogOpen(true)}
        onClickGetInviteLink={() => setInviteLinkDialogOpen(true)}
      />
      <TableBox sx={{ px: 5, py: 2 }}>{renderTable()}</TableBox>
      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => {
          setInviteDialogOpen(false);
          setBulkInviteResults(null);
        }}
        onSubmit={handleInviteUser}
        roleOptions={editRoleOptions}
        defaultRole={UserSubaccountRole.Texter}
        enableMultipleInvites={true}
        bulkInviteResults={bulkInviteResults}
      />
      <InviteLinkDialog
        open={inviteLinkDialogOpen}
        onClose={() => setInviteLinkDialogOpen(false)}
      />
      <EditUserDialog
        user={userToEdit}
        open={!!userToEdit}
        onClose={() => setUserToEdit(null)}
        roleOptions={editRoleOptions}
        onSubmit={handleUpdateUserRole}
      />
    </PageContainer>
  );
}
